import React, { useCallback, useContext, useEffect, useMemo, useRef, useState, memo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { AdminPanelPage } from '../AdminPanelPage';
import { useLocalizedStrings } from '../../Localization';
import { Loading, Tabs, Select, SelectOptionType, ResizablePanel, BreadcrumbProps, Card, CardBody } from '@aidkitorg/component-library';
import { usePost, useRoboScreener, useAPIPost, get_rs_host } from '../../API';
import { NavigationNode } from '@aidkitorg/types/lib/translation/permissions';
import { ModularQuestionPage } from '../../ModularQuestionPage';
import { ApplicantProvider } from '../../Contexts/ApplicantContext';
import { Section } from "@aidkitorg/types/lib/legacy/airtable";
import InterfaceContext, { ConfigurationContext, PublicConfigurationContext } from '../../Context';
import { useAuthorizedCommsChannels } from '../../Applicant/Comms';
import { ApplicantComms } from '../../Applicant/Comms';
import Notes from '../../Applicant/Notes';
import History from '../../Applicant/History';
import { AdminTab } from '../../Applicant/AdminTab';
import { FraudFlags } from '../../Applicant/FraudFlags';
import { ApplicantMeetings } from '../../Applicant/Meetings';
import { ThirdPartyCheckStatusTab } from '../../Applicant/ThirdPartyCheckStatus';
import { useAsyncEffect } from '../../Hooks/AsyncEffect';
import { SubTab } from '../../Components/ThreeColumnPage';
import { LoggedInConfig } from '@aidkitorg/types/lib/config';
import { useRelatedCases } from '../../SupportUtil';
import { get_deployment } from '../../API';
import { snakeToEnglish, useInterval } from '../../Util';
import { captureException } from '@sentry/react';
import { ApplicantPaymentPage } from '../../Applicant/PaymentPage';

type AdminApplicantPageProps = {
  uid: string;
  subsurvey?: string;
};

/**
 * Renders applicant content within the admin panel.
 * Reuses ApplicantPage logic but with admin panel layout (no left nav, dropdown for sections).
 */
export function AdminApplicantPage({ uid: propUid, subsurvey: propSubsurvey }: AdminApplicantPageProps) {
  const L = useLocalizedStrings();
  const history = useHistory();
  const { pathname } = useLocation();
  const config = useContext(ConfigurationContext);
  const publicConfig = useContext(PublicConfigurationContext);
  const interfaceContext = useContext(InterfaceContext);
  const commsChannels = useAuthorizedCommsChannels({ config });

  const [nav, setNav] = useState<NavigationNode[]>([]);
  const [info, setInfo] = useState<{ [key: string]: any }>({});
  const [fetchedInfo, setFetchedInfo] = useState<{ [key: string]: any }>({});
  const [isLoading] = useState(false);
  const [subsurvey, setSubsurvey] = useState(propSubsurvey);
  const [surveySections, setSurveySections] = useState<{ sections: Section[] }>({ sections: [] });
  const [activeSections, setActiveSections] = useState<{ sections: Section[] }>({ sections: [] });
  const [disallowed, setDisallowed] = useState<string[] | null>(null);
  const [loggedInConfig, setLoggedInConfig] = useState<LoggedInConfig | undefined>();
  const [fraudFlagsLength, setFraudFlagsLength] = useState<number | null>(null);

  const getSurvey = usePost("/survey");
  const getNav = usePost("/navigation");
  const getLoggedInConfig = usePost('/program/logged_in_configuration');
  const getEverything = usePost('/applicant/get_everything');
  const saveInfoRS = useAPIPost(get_rs_host() + "/compute_and_save", { includeTokenInData: true });

  const pendingChanges = useRef<Record<string, any>>({});
  const pendingTimeout = useRef<NodeJS.Timeout | null>(null);
  const blockFurtherEdits = useRef<any>(null);
  const shouldFastForward = useRef<boolean | undefined>();

  const uid = propUid;

  // Load navigation and survey
  useAsyncEffect(async () => {
    const [response, sections] = await Promise.all([
      getNav({ applicant: uid }),
      getSurvey({}) as any
    ]);

    setNav(response.navigation);
    setSurveySections(sections);
  }, [uid]);

  // Sync subsurvey state with prop changes (when URL changes)
  useEffect(() => {
    if (propSubsurvey) {
      setSubsurvey(propSubsurvey);
    }
  }, [propSubsurvey]);

  // Set disallowed roles from config
  useEffect(() => {
    // temporary convention till we can implement explicit RBAC.
    // this scoops up all "no-[name]" tags and lets us use em
    // in the tab array below to more easily control what is shown.
    //
    // TODO: Remove this once we have a better RBAC!
    if (config.roles) {
      setDisallowed(Array.from((config.roles || []) as string[]).filter((r) => r.startsWith('no-')).map(r => r.replace('no-', '')));
    }
  }, [config.roles]);

  // Determine subsurvey to render
  useAsyncEffect(async () => {
    let subsurveyToRender = propSubsurvey;

    const findFirstSubsurvey = (nav: NavigationNode[]): string | undefined => {
      for (const node of nav) {
        if (typeof node.name === 'string' && node.name.startsWith('/p/')) {
          return node.name.slice(3);
        }
        if (node.subnodes) {
          const found = findFirstSubsurvey(node.subnodes);
          if (found) return found;
        }
      }
    };

    if (!subsurveyToRender && nav.length > 0) {
      subsurveyToRender = findFirstSubsurvey(nav);
    }

    if (subsurveyToRender) {
      setSubsurvey(subsurveyToRender);
    }
  }, [nav, propSubsurvey]);

  // Load logged in config once (doesn't change per applicant)
  useAsyncEffect(async () => {
    const loggedInConfigResponse = await getLoggedInConfig({});
    setLoggedInConfig(loggedInConfigResponse);
  }, []);

  // Load applicant data
  const refreshInProgress = useRef<Promise<any> | null>(null);
  const currentApplicantId = useRef(uid);

  async function refreshApplicant() {
    if (refreshInProgress.current) return;

    let app;
    try {
      const applicantId = currentApplicantId.current;
      refreshInProgress.current = getEverything({ uid: applicantId });
      app = await refreshInProgress.current;

      if (currentApplicantId.current !== applicantId) {
        // This info is for the wrong applicant
        return;
      }

    } finally {
      refreshInProgress.current = null;
    }

    // We may have changed things since in the await
    if (Object.keys(pendingChanges.current).length > 0) {
      return;
    }

    if (app) {
      setInfo(app.info || {});
      setFetchedInfo(app.info || {});
    }
  }

  useEffect(() => {
    refreshApplicant();
  }, []);

  useEffect(() => {
    currentApplicantId.current = uid;
  }, [uid]);

  // Update active sections when nav, surveySections, or subsurvey changes
  useEffect(() => {
    if (!subsurvey || nav.length === 0 || surveySections.sections.length === 0) {
      return;
    }

    // Determine active sections based on subsurvey
    // Match sections by "English Content" property (section title), not by ID
    const findSubsurveySections = (nodes: NavigationNode[]): Section[] | null => {
      for (const node of nodes) {
        if (node.name === '/p/' + subsurvey && node.subnodes) {
          shouldFastForward.current = node.shouldFastForward;
          blockFurtherEdits.current = node.blockFurtherEdits || null;

          const sections: Section[] = [];
          for (const subnode of node.subnodes) {
            // Match by section title (English Content)
            const c = surveySections.sections.filter((s: Section) =>
              s["English Content"] === subnode.name ||
              s["English Content"] === (subnode.name as any).en
            );

            if (c.length === 1) {
              sections.push(c[0]);
            } else if (c.length > 0) {
              // Perform string matching on path injected into sectionIdentifier to differentiate between sections with the same title
              const sectionPath = subnode.sectionPath;
              let matchedContent: any[] = [];

              if (sectionPath && sectionPath !== "") {
                // The Subsurvey kind in matched content should have the path for the subsurvey
                matchedContent = c.filter((s: Section) =>
                  s["Questions"].find((q) => (q["Kind"] === 'Subsurvey' && q["Question"] === sectionPath))
                );
              }

              // If none of this works, default to pushing the first content found
              if (matchedContent.length > 0) {
                sections.push(matchedContent[0]);
              } else {
                sections.push(c[0]);
              }
            }
          }
          return sections;
        }
        if (node.subnodes) {
          const found = findSubsurveySections(node.subnodes);
          if (found) return found;
        }
      }
      return null;
    };

    const sections = findSubsurveySections(nav) || [];
    setActiveSections({ sections });
  }, [subsurvey, nav, surveySections]);

  // Save info with debouncing
  function saveInfo(update: Record<string, any>) {
    // Figure out what changed (note, we know that "deleted" keys are set as '' so we don't
    // need to search for keys that have disappeared)
    setInfo(update);
    const changes = Object.keys(update).reduce((prev, k: string) => {
      if (update[k] !== info[k]) {
        prev[k] = update[k];
      }
      return prev;
    }, {} as Record<string, any>);

    if (Object.keys(changes).length) {
      // Update any pending changes
      Object.assign(pendingChanges.current, changes);

      // Debounce saving by 1 second
      if (pendingTimeout.current) {
        clearTimeout(pendingTimeout.current);
      }
      pendingTimeout.current = setTimeout(async () => {
        const toSave = { ...pendingChanges.current };

        const result = await saveInfoRS({
          changedKeys: toSave,
          uid,
          deploymentKey: get_deployment()
        });
        if (!result) {
          captureException("Error saving info", {
            extra: {
              uid,
              program: get_deployment(),
              keys: Object.keys(toSave)
            }
          });
        }
        // We need to delete the keys that we attempted to save
        // even if there was an error else this while loop will
        // never terminate
        if (result?.error) {
          console.error("Error saving", result.error, Object.keys(pendingChanges.current).join(","));
          captureException(result.error, { extra: { uid, deploymentKey: get_deployment() } });
        }
        for (const k of Object.keys(toSave)) {
          if (pendingChanges.current[k] === toSave[k]) {
            delete pendingChanges.current[k];
          }
        }
        // TODO: eventually remove this! We are putting this in to allow BRF RS calls to take longer without erroring.
        // We would like to remove this once we have made smarter changes to RS.
      }, get_deployment() === 'brf' ? 1500 : 1000);
    }
  }

  // Refresh every 3 seconds unless there are pending changes
  useInterval(() => {
    if (Object.keys(pendingChanges.current).length > 0) {
      console.log("Pending changes", pendingChanges.current);
      return;
    }
    refreshApplicant();
  }, 3000);

  const relatedSupportCases = useRelatedCases(uid);

  // Extract subsurvey options from navigation
  // Just use the actual subsurvey path names as labels
  const subsurveyOptions = useMemo<SelectOptionType[]>(() => {
    const options: SelectOptionType[] = [];

    const extractSubsurveys = (nodes: NavigationNode[]) => {
      for (const node of nodes) {
        if (typeof node.name === 'string' && node.name.startsWith('/p/')) {
          const subsurveyPath = node.name.slice(3);
          const displayLabel = subsurveyPath
            .split('/')
            .map(part => snakeToEnglish(part))
            .join(' / ');
          options.push({
            value: subsurveyPath,
            label: displayLabel
          });
        }
        if (node.subnodes) {
          extractSubsurveys(node.subnodes);
        }
      }
    };

    extractSubsurveys(nav);
    return options;
  }, [nav]);

  const selectedSubsurveyOption = useMemo(
    () => subsurveyOptions.find(option => option.value === subsurvey) ?? subsurveyOptions[0],
    [subsurveyOptions, subsurvey]
  );

  const handleSubsurveyChange = useCallback((option: SelectOptionType) => {
    const newSubsurvey = option.value;
    setSubsurvey(newSubsurvey);
    history.push(`/admin-panel/a/${uid}/${newSubsurvey}`);
  }, [history, uid]);

  // Build tabs (reusing ApplicantPage logic)
  const subtabs: SubTab[] = useMemo(() => {
    if (disallowed?.includes('tabs')) return [];

    return [
      {
        key: 'notes',
        name: L.applicant.notes.title,
        show: disallowed && !disallowed.includes('notes'),
        children: <Notes uid={uid} canDeleteNotes={true} relatedSupportCases={relatedSupportCases} />
      },
      ...(commsChannels.map(channel => ({
        key: 'comms' + channel,
        name: 'Comms' + (channel !== 'default' ? ` (${channel})` : ''),
        show: disallowed && !disallowed.includes('comms') && commsChannels && commsChannels.length > 0,
        children: <ApplicantComms
          key={'comms-' + channel}
          uid={uid}
          info={fetchedInfo}
          setUnhandledCount={() => {}}
          canSendComms={true}
          channel={channel}
          relatedSupportCases={relatedSupportCases?.filter(sc => sc.channel === channel)}
        />
      }))),
      {
        name: 'IA Notes',
        key: 'ia-notes',
        show: disallowed && ((config.roles || '').indexOf('internal audit') !== -1),
        children: <Notes uid={uid} canDeleteNotes={true} internalAudit={true} />
      },
      {
        key: 'meetings',
        name: L.applicant.meeting.meetings,
        show: disallowed && !disallowed.includes('meetings') && loggedInConfig?.comms?.videoCalling?.enabled,
        children: <ApplicantMeetings applicantId={uid} info={fetchedInfo} />
      },
      {
        key: 'history',
        name: L.applicant.history.title,
        show: disallowed && !disallowed.includes('history'),
        children: <History uid={uid} canViewChanges={true} />
      },
      {
        key: 'debug',
        name: 'Debug',
        show: disallowed && !disallowed.includes('debug'),
        children: <AdminTab uid={uid} />
      },
      {
        key: 'fraud',
        name: L.applicant.fraud.title + (fraudFlagsLength !== null ? ` (${fraudFlagsLength})` : ''),
        show: disallowed && !disallowed.includes('fraud') && loggedInConfig?.fraud?.enableTab && ((config.roles || '').includes('admin') || (config.roles || '').includes('reviewer')),
        children: <FraudFlags uid={uid} setLength={(l) => setFraudFlagsLength(l)} />
      },
      {
        key: 'tpcs',
        name: 'Third Party Checks',
        show: disallowed && !disallowed.includes('history') && (
          publicConfig?.capabilities?.thirdPartyCheckStatusTab && config.roles?.some((role: string) => {
            return publicConfig?.capabilities?.thirdPartyCheckStatusTab?.includes(role)
          })) || false,
        children: <ThirdPartyCheckStatusTab uid={uid} info={fetchedInfo} saveInfo={saveInfo} isAdmin={config.roles?.includes('admin')} />
      }
    ].filter(tab => tab.show);
  }, [uid, disallowed, commsChannels, fetchedInfo, loggedInConfig, fraudFlagsLength, relatedSupportCases, config.roles, publicConfig]);

  const tabsConfig = useMemo(() => (
    subtabs.map(tab => ({
      key: tab.key,
      name: tab.name,
      content: (
        <div className="p-4">
          {tab.children}
        </div>
      )
    }))
  ), [subtabs]);

  const applicantContext = useMemo(() => {
    return {
      info,
      update: saveInfo,
      refreshInfo: refreshApplicant
    };
  }, [info, saveInfo, refreshApplicant]);


  const containerRef = useRef<HTMLDivElement | null>(null);
  const applicantInfoPanelLabel = L.menu?.applicant_info_panel;

  const breadcrumbs = useMemo(() => {
    const crumbs: BreadcrumbProps['items'] = [];

    // Use legal_name from info, fallback to applicantTitle, then 'Applicant'
    const displayName = info?.legal_name || L.applicant.applicant;

    crumbs.push({
      label: displayName,
      href: `/admin-panel/a/${uid}`
    });

    if (selectedSubsurveyOption?.label) {
      const labelText = typeof selectedSubsurveyOption.label === 'string'
        ? selectedSubsurveyOption.label
        : selectedSubsurveyOption.value as string;
      crumbs.push({ label: labelText });
    }

    return crumbs;
  }, [info?.legal_name, uid, selectedSubsurveyOption]);

  const displayName = useMemo(() =>
    info?.legal_name || L.applicant.applicant,
  [info?.legal_name, L.applicant.applicant]
  );

  const pageTitle = useMemo(() =>
    L.admin_panel.reviews.reviewing_applicant.replace('{name}', displayName),
  [L.admin_panel.reviews.reviewing_applicant, displayName]
  );

  const pageDescription = useMemo(() =>
    L.admin_panel.reviews.review_instructions,
  [L.admin_panel.reviews.review_instructions]
  );

  // Create page action buttons (section selector)
  const pageActionButtons = useMemo(() => {
    // Only show section selector if multiple sections available
    if (subsurveyOptions.length <= 1) return undefined;

    return [
      <div key="section-selector" className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
          {L.admin_panel.reviews.jump_to_section}:
        </span>
        <div className="min-w-[200px]">
          <Select
            label={L.admin_panel.reviews.jump_to_section}
            hideLabel
            description=""
            options={subsurveyOptions}
            value={selectedSubsurveyOption}
            onChange={handleSubsurveyChange}
            placeholder="Select a section"
            portal={true}
          />
        </div>
      </div>
    ];
  }, [subsurveyOptions, selectedSubsurveyOption, handleSubsurveyChange, L.admin_panel.reviews.jump_to_section]);

  return (
    <ApplicantProvider context={applicantContext}>
      <AdminPanelPage
        title={pageTitle}
        description={pageDescription}
        breadcrumbs={breadcrumbs}
        pageActionButtons={pageActionButtons}
      >
        <div className="flex relative items-stretch w-full min-h-0" ref={containerRef}>
          {/* Main content area */}
          <div className="flex-1 pr-4 min-w-0 min-h-0">
            <Card className="rounded-md shadow-sm max-h-[calc(100vh-14rem)] overflow-hidden">
              <CardBody className="h-full overflow-auto">
                {/* Main content */}
                <div className="relative z-0">
                  <Loading loading={isLoading}>
                    {pathname.startsWith('/admin-panel/payments/')
                      ? <ApplicantPaymentPage applicant={uid} />
                      : <ModularQuestionPage
                        sections={activeSections as any}
                        uid={uid}
                        viewInfo={{ readonly: undefined }}
                        submit={async () => {}}
                        saveInfo={async () => {}}
                        saveAuth={() => {}}
                        blockFurtherEdits={blockFurtherEdits.current || undefined}
                        shouldFastForward={shouldFastForward.current === undefined ? undefined : shouldFastForward}
                        noNavBar={subsurvey ? undefined : true}
                        sequential={true}
                        Viewer='screener'
                      />
                    }
                  </Loading>
                </div>
              </CardBody>
            </Card>
          </div>

          {/* Right sidebar tabs */}
          {subtabs.length > 0 && (
            <div className="resize-panel-container sticky top-0 self-stretch max-h-[calc(100vh-14rem)] overflow-visible">
              <ResizablePanel
                side="right"
                label={applicantInfoPanelLabel}
                dir={interfaceContext?.direction ?? 'ltr'}
                initialWidthPercent={25}
                displayDensity="normal"
                viewportRef={containerRef}
                className="h-full"
              >
                <div className="relative -ml-6">
                  <style>{`
                    .resize-panel-container button[data-testid="resizable-panel-toggle"] {
                      left: calc(100% - 0.5rem) !important;
                      right: auto !important;
                      top: 0.75rem !important;
                    }
                  `}</style>
                  <Card className="rounded-md rounded-l-none border-l-0 h-full overflow-hidden">
                    <CardBody className="h-full p-0">
                      <div className="flex h-full overflow-hidden">
                        <div className="flex-1 overflow-auto min-w-0">
                          <Tabs
                            className="w-full"
                            justify="left"
                            tabs={tabsConfig}
                          />
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </div>
              </ResizablePanel>
            </div>
          )}
        </div>
      </AdminPanelPage>
    </ApplicantProvider>
  );
}
